/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import {
  DefaultList,
  Dropdown,
  DropdownList,
  DropdownUserAssistance,
  EmptyResults,
  LinkItem,
  preventDefault
} from '#PRIVATE_SelectCommon';
import { Separator } from '#UNSAFE_Separator';
import { InlineUserAssistance, UserAssistanceDensityType } from '#UNSAFE_UserAssistance';
import { TabbableModeContext } from '#hooks/UNSAFE_useTabbableMode';
import { useTranslationBundle } from '#hooks/UNSAFE_useTranslationBundle';
import { BundleType } from '#resources/nls/bundle';
import { ComponentProps } from 'preact';
import { Ref } from 'preact/hooks';

type PickedDefaultListProps<K extends string | number, D extends Record<string, any>> = Pick<
  ComponentProps<typeof DefaultList<K, D>>,
  | 'currentKey'
  | 'data'
  | 'itemRenderer'
  | 'itemText'
  | 'onCurrentKeyChange'
  | 'onItemAction'
  | 'onLoadRange'
  | 'searchText'
  | 'selectedKeys'
>;

type PickedDropdownProps = Pick<
  ComponentProps<typeof Dropdown>,
  'isOpen' | 'onAutoDismiss' | 'onPosition'
>;

type PickedInlineUserAssistanceProps = Pick<
  ComponentProps<typeof InlineUserAssistance>,
  'assistiveText' | 'helpSourceLink' | 'helpSourceText'
>;

type Props<K extends string | number, D extends Record<string, any>> = PickedDefaultListProps<
  K,
  D
> &
  PickedDropdownProps &
  PickedInlineUserAssistanceProps & {
    dropdownId: string;
    dropdownRef: Ref<HTMLDivElement>;
    isAddToListShown?: boolean;
    isAdvancedSearchShown: boolean;
    isDisabled?: boolean;
    isDropdownAbove: boolean;
    isEmptyResults: boolean;
    isReadonly?: false;
    isUserFiltering: boolean;
    label: string;
    mainFieldRef: Ref<HTMLDivElement>;
    onAddToListAction?: () => void;
    onAdvancedSearchAction?: () => void;
    userAssistanceDensity?: UserAssistanceDensityType;
  };

export function SelectSingleDesktopDropdown<
  K extends string | number,
  D extends Record<string, any>
>({
  assistiveText,
  currentKey,
  data,
  dropdownId,
  dropdownRef,
  helpSourceLink,
  helpSourceText,
  isAddToListShown,
  isAdvancedSearchShown,
  isDisabled,
  isDropdownAbove,
  isEmptyResults,
  isOpen,
  isReadonly,
  isUserFiltering,
  itemRenderer,
  itemText,
  label,
  mainFieldRef,
  onAddToListAction,
  onAdvancedSearchAction,
  onAutoDismiss,
  onCurrentKeyChange,
  onItemAction,
  onLoadRange,
  onPosition,
  searchText,
  selectedKeys,
  userAssistanceDensity
}: Props<K, D>) {
  const translations = useTranslationBundle<BundleType>('@oracle/oraclejet-preact');

  const dropdownInlineUserAssistance =
    isOpen &&
    !isDropdownAbove &&
    !isDisabled &&
    !isReadonly &&
    (assistiveText || helpSourceLink) &&
    (userAssistanceDensity === 'efficient' || userAssistanceDensity === 'reflow') ? (
      <TabbableModeContext.Provider value={{ isTabbable: false }}>
        <DropdownUserAssistance
          assistiveText={assistiveText}
          fieldLabel={label}
          helpSourceLink={helpSourceLink}
          helpSourceText={helpSourceText}
          userAssistanceDensity={userAssistanceDensity}
        />
      </TabbableModeContext.Provider>
    ) : undefined;

  const dropdownListContent = isEmptyResults ? (
    isAddToListShown || isAdvancedSearchShown ? undefined : (
      <EmptyResults />
    )
  ) : (
    <DefaultList
      aria-label={label ?? ''}
      currentItemVariant="highlight"
      currentKey={currentKey}
      data={data}
      itemRenderer={itemRenderer}
      itemText={itemText}
      onCurrentKeyChange={onCurrentKeyChange}
      onItemAction={onItemAction}
      onLoadRange={onLoadRange}
      searchText={isUserFiltering ? searchText : undefined}
      selectedKeys={selectedKeys}
      selectionMode="single"
    />
  );

  const addToListContent = isAddToListShown ? (
    <LinkItem isHighlighted={isEmptyResults} onAction={onAddToListAction}>
      {translations.select_addToList()}
    </LinkItem>
  ) : undefined;

  const advancedSearchContent = isAdvancedSearchShown ? (
    <>
      {!isEmptyResults && (
        <div onMouseDown={preventDefault}>
          <Separator />
        </div>
      )}
      <LinkItem isHighlighted={isEmptyResults} onAction={onAdvancedSearchAction}>
        {translations.select_moreSearchOptions()}
      </LinkItem>
    </>
  ) : undefined;

  return (
    <Dropdown
      anchorRef={mainFieldRef}
      dropdownRef={dropdownRef}
      id={dropdownId}
      isOpen={isOpen}
      onAutoDismiss={onAutoDismiss}
      onPosition={onPosition}>
      <DropdownList
        hasBottomGap={dropdownInlineUserAssistance === undefined}
        isLoading={data === null}>
        {dropdownListContent}
        {addToListContent}
        {advancedSearchContent}
      </DropdownList>
      {dropdownInlineUserAssistance}
    </Dropdown>
  );
}
